import { Modal } from 'components/Modal';
import { ChangeEvent, useContext, useEffect, useMemo } from 'react';
import filter from 'lodash/filter';
import { Button } from 'components/Button';
import { ModalBody } from 'components/Modal/ModalBody';
import { ModalActions } from 'components/Modal/ModalActions';
import { ArrowLeftLongIcon } from 'icons/outline';
import { Box } from 'components/Box';
import { SelectHiredPeople } from 'features/SelectHiredPeople';
import noop from 'lodash/noop';
import uniqBy from 'lodash/uniqBy';
import { HiringUpdateFlowContext } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateContext';
import { HiringUpdateFlowSteps } from 'features/EmployerHiringUpdateFlow/EmployerHiringUpdateFlow.constants';
import { InputInstructions } from 'features/EmployerHiringUpdateFlow/InputInstructions';
import { useEmployerJobApplications } from 'hooks/useEmployerJobApplications';
import map from 'lodash/map';
import debounce from 'lodash/debounce';

export const HiredWhoElseStep = () => {
  const {
    open,
    step,
    goToNextStep,
    goToPreviousStep,
    hiredApplicants,
    setHiredApplicants,
    hiredUserListings,
    setHiredUserListings,
    hiredNonApplicantName,
    setHiredNonApplicantName,
  } = useContext(HiringUpdateFlowContext);

  const { jobApplications, loading, fetchJobApplications } = useEmployerJobApplications({
    initialQueryProps: {
      includes: ['userProfile'],
      where: {
        notStatus: 'hired',
      },
    },
  });

  const jobApplicationsPrimitive = map(jobApplications, 'id').join(',');

  const applicants = useMemo(
    () => map(jobApplications, (jobApplication) => jobApplication.userProfile()),
    [jobApplicationsPrimitive],
  );

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    fetchJobApplications({
      includes: ['userProfile'],
      where: {
        applicantName: value,
        notStatus: 'hired',
      },
    });
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value, checked } = event.target;

    const applicant = applicants.find((item) => item.id === value);

    setHiredUserListings((prev) =>
      checked && Boolean(applicant?.id)
        ? [
            ...prev,
            {
              userProfileId: applicant.id,
            },
          ]
        : filter(prev, (item) => item.userProfileId !== value),
    );
    setHiredApplicants((prev) =>
      checked && Boolean(applicant?.id)
        ? uniqBy([...prev, applicant], 'id')
        : filter(prev, (item) => item.id !== value),
    );
  };

  const handleNonApplicantNameChange = (value: string) => {
    setHiredNonApplicantName(value);

    const hiredUserListingsWithoutNonApplicant = filter(hiredUserListings, (item) =>
      Boolean(item.userProfileId),
    );

    setHiredUserListings([
      ...hiredUserListingsWithoutNonApplicant,
      {
        userProfileName: value,
      },
    ]);
  };

  useEffect(() => {
    if (open) {
      fetchJobApplications({
        includes: ['userProfile'],
        where: {
          notStatus: 'hired',
        },
      });
    }
  }, [open]);

  return (
    <Modal
      panelProps={{
        className: 'sm:!max-w-[476px]',
      }}
      open={open && step === HiringUpdateFlowSteps.HIRED_WHO_ELSE}
      heading={<span className="text-primary-pure-blue">Who else did you hire?</span>}
      actionRequired
      onClose={noop}
    >
      <ModalBody>
        <InputInstructions>
          <span className="block mx-auto max-w-[315px]">
            If you hired multiple people for this role, select all the people that you hired.
          </span>
        </InputInstructions>

        <Box className="!py-4 !px-4 mt-6">
          <SelectHiredPeople
            applicants={applicants}
            loading={loading}
            onSearchChange={debounce(handleSearchChange, 500)}
            selectedApplicants={hiredApplicants}
            onSelectionChange={handleChange}
            nonApplicantName={hiredNonApplicantName}
            onNonApplicantNameChange={handleNonApplicantNameChange}
          />
        </Box>
      </ModalBody>

      <ModalActions>
        <Button
          type="button"
          size="lg"
          fullWidth
          onClick={goToNextStep}
          disabled={
            hiredNonApplicantName === '' ||
            (hiredNonApplicantName === null && !hiredApplicants.length)
          }
        >
          Confirm
        </Button>
        <Button type="button" size="lg" variant="outline" onClick={goToPreviousStep} fullWidth>
          <ArrowLeftLongIcon className="mr-2" />
          <span>Go back</span>
        </Button>
      </ModalActions>
    </Modal>
  );
};
